import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// API Base URLs
const SKJOLD_API_BASE = 'https://prod-skjold-api.azurewebsites.net/api';
const EMAIL_API_BASE = 'https://nsg-e2e-webapp-notify.azurewebsites.net/api/email';
const KONCIV_API_BASE = process.env.REACT_APP_KONCIV_API;
const SKJOLD_BACKEND_API_BASE = process.env.REACT_APP_SKJOLD_BACKEND_SERVICE_API;

// Get subscription key from environment
const SUBSCRIPTION_KEY = process.env.REACT_APP_SUBSCRIPTION_KEY || '';
const KONCIV_SUBSCRIPTION_KEY = process.env.REACT_APP_KONCIV_SUBSCRIPTION_KEY || '';

// Types
export interface PartnerDataRequest {
  partner: string;
  subscriptionKey: string;
}

export interface PartnerDataResponse {
  cms: any[];
  branding: any[];
  value: string;
}

export interface AnalyzeMessageRequest {
  msg: string;
  source: string;
  partner: string;
  number: string;
  subscriptionKey: string;
  ocr: boolean;
  language: string;
}

export interface AnalyzeMessageResponse {
  Classification: 'Danger' | 'Warning' | 'Safe';
  Content: string;
  CaseId: number;
}

export interface EmailRecipient {
  Email: string;
  Name: string;
}

export interface SendEmailRequest {
  To: EmailRecipient[];
  Subject: string;
  Content: string;
  From: EmailRecipient;
}

export interface LogoRequest {
  url: string;
  authorization?: string;
}

// Konciv API types
export interface KoncivTokenRequest {
  email: string;
  password: string;
}

export interface KoncivTokenResponse {
  access_token?: string;
  accessToken?: string;
  [key: string]: any;
}

export interface Article {
  id: string;
  header: string;
  subHeader: string;
  mainBody: string;
  image: string;
}

export interface GetArticlesRequest {
  accessToken: string;
  partnerName: string;
}

export interface GetCmsDataRequest {
  accessToken: string;
  partnerName?: string;
}

export interface CmsDataResponse {
  cms: any[];
  languages: { name: string; code: string }[];
}

export interface VerifyInvitationCodeRequest {
  invitationCode: string;
}

export interface VerifyInvitationCodeResponse {
  success: boolean;
  partnerName: string;
  accessToken: string;
  message?: string;
}

export interface LogActiveUserRequest {
  partnerName: string;
  invitationCode: string;
  deviceInfo?: {
    platform: string;
    osVersion?: string;
  };
}

export interface LogActiveUserResponse {
  success: boolean;
  message?: string;
}

export interface AnalyzeImageRequest {
  file: File;
  source: string;
  partner: string;
  number: string;
  language: string;
}

// Create the API service
export const skjoldApi = createApi({
  reducerPath: 'skjoldApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }), // We'll override baseUrl per endpoint
  tagTypes: ['Partner', 'Analysis', 'Email', 'KoncivToken', 'Articles', 'CmsData', 'InvitationCode', 'ActiveUser'],
  endpoints: (builder) => ({
    // Get partner data
    getPartnerData: builder.mutation<PartnerDataResponse, { partner: string }>({
      query: ({ partner }) => ({
        url: `${SKJOLD_API_BASE}/getPartner`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          partner,
          subscriptionKey: SUBSCRIPTION_KEY,
        }).toString(),
      }),
      invalidatesTags: ['Partner'],
    }),

    // Analyze message
    analyzeMessage: builder.mutation<AnalyzeMessageResponse, Omit<AnalyzeMessageRequest, 'subscriptionKey'>>({
      query: (data) => ({
        url: `${SKJOLD_API_BASE}/v2/getResponse`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
        body: new URLSearchParams({
          msg: data.msg,
          source: data.source,
          partner: data.partner,
          number: data.number,
          subscriptionKey: SUBSCRIPTION_KEY,
          ocr: data.ocr.toString(),
          language: data.language,
        }).toString(),
      }),
      invalidatesTags: ['Analysis'],
    }),

    // Send email
    sendEmail: builder.mutation<void, SendEmailRequest>({
      query: (data) => ({
        url: `${EMAIL_API_BASE}/send`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data,
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: ['Email'],
    }),

    // Get logo
    getLogo: builder.query<Blob, LogoRequest>({
      query: ({ url, authorization }) => ({
        url,
        method: 'GET',
        headers: authorization ? { Authorization: authorization } : {},
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Generate Konciv access token
    generateKoncivToken: builder.mutation<KoncivTokenResponse, KoncivTokenRequest>({
      query: ({ email, password }) => ({
        url: `${KONCIV_API_BASE}/oauth/token`,
        method: 'POST',
        headers: {
          'Ocp-Apim-Subscription-Key': KONCIV_SUBSCRIPTION_KEY,
          'Content-Type': 'application/json',
        },
        body: {
          username: email,
          password: password,
        },
      }),
      invalidatesTags: ['KoncivToken'],
    }),

    // Get articles by partner
    getArticlesByPartner: builder.mutation<Article[], GetArticlesRequest>({
      query: ({ accessToken, partnerName }) => ({
        url: `${SKJOLD_BACKEND_API_BASE}/articles`,
        method: 'GET',
        headers: {
          'Ocp-Apim-Subscription-Key': KONCIV_SUBSCRIPTION_KEY,
          'Content-Type': 'application/json',
        },
        params: {
          accessToken,
          partnerName,
        },
      }),
      invalidatesTags: ['Articles'],
    }),

    getCmsData: builder.mutation<CmsDataResponse, GetCmsDataRequest>({
      query: ({ accessToken, partnerName }) => ({
        url: `${SKJOLD_BACKEND_API_BASE}/cms`,
        method: 'GET',
        headers: {
          'Ocp-Apim-Subscription-Key': KONCIV_SUBSCRIPTION_KEY,
          'Content-Type': 'application/json',
        },
        params: {
          accessToken,
          ...(partnerName && { partnerName }),
        },
      }),
      invalidatesTags: ['CmsData'],
    }),

    // Verify invitation code
    verifyInvitationCode: builder.mutation<VerifyInvitationCodeResponse, VerifyInvitationCodeRequest>({
      query: ({ invitationCode }) => ({
        url: `${SKJOLD_BACKEND_API_BASE}/verify-invitation`,
        method: 'POST',
        headers: {
          'Ocp-Apim-Subscription-Key': KONCIV_SUBSCRIPTION_KEY,
          'Content-Type': 'application/json',
        },
        body: {
          invitationCode,
        },
      }),
      invalidatesTags: ['InvitationCode'],
    }),

    analyzeImage: builder.mutation<AnalyzeMessageResponse, AnalyzeImageRequest>({
      query: (data) => {
        const formData = new FormData();
        formData.append('subscriptionKey', SUBSCRIPTION_KEY);
        formData.append('file', data.file);
        formData.append('source', data.source);
        formData.append('number', data.number || '');
        formData.append('partner', data.partner);
        formData.append('language', data.language);
        formData.append('ocr', 'true');

        return {
          url: `${SKJOLD_API_BASE}/v2/getResponseImage`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Analysis'],
    }),

    logActiveUser: builder.mutation<LogActiveUserResponse, LogActiveUserRequest>({
      query: ({ partnerName, invitationCode, deviceInfo }) => ({
        url: `${SKJOLD_BACKEND_API_BASE}/log-active-user`,
        method: 'POST',
        headers: {
          'Ocp-Apim-Subscription-Key': KONCIV_SUBSCRIPTION_KEY,
          'Content-Type': 'application/json',
        },
        body: {
          partnerName,
          invitationCode,
          deviceInfo,
          timestamp: new Date().toISOString(),
        },
      }),
      invalidatesTags: ['ActiveUser'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetPartnerDataMutation,
  useAnalyzeMessageMutation,
  useAnalyzeImageMutation,
  useSendEmailMutation,
  useGetLogoQuery,
  useLazyGetLogoQuery,
  useGenerateKoncivTokenMutation,
  useGetArticlesByPartnerMutation,
  useGetCmsDataMutation,
  useVerifyInvitationCodeMutation,
  useLogActiveUserMutation,
} = skjoldApi;
